/**
 * Server-side function to generate theme CSS variables
 * This prevents FOUC (Flash of Unstyled Content) by injecting styles before React hydrates
 */

import { DEFAULT_DATA_THEME } from 'app/config/disporty'
import { parseTheme } from './parseConfigTheme'
import { rgbaToHsla } from 'lib/rgbaToHsla'

export interface ThemeStyles {
    dataTheme?: string
    cssVariables: string
    enableNoiseTexture: boolean
}

/**
 * Generates CSS variables and data-theme attribute for the given config
 * Returns a string that can be injected as inline styles or in a <style> tag
 */
export function generateThemeStyles(config: IDomainConfig | null | undefined): ThemeStyles {
    if (!config?.theme) {
        return {
            dataTheme: DEFAULT_DATA_THEME,
            cssVariables: '',
            enableNoiseTexture: true
        }
    }

    try {
        const cardData = parseTheme(config.theme)
        const { branding } = cardData
        const enableNoiseTexture = branding?.enable_noise_texture ?? true
        const { use_preset_theme, preset_theme } = branding

        if (use_preset_theme && preset_theme) {
            return {
                dataTheme: preset_theme,
                cssVariables: '',
                enableNoiseTexture
            }
        }

        // Generate CSS variables for custom theme
        const {
            page_bg,
            secondary_bg,
            primary,
            text,
            info,
            success,
            error,
            warning,
            softHighlight,
            ring,
            border,
            input,
            soft_text,
            card,
            is_gradient_background
        } = branding

        const setThemeProperty = (name: string, value: { color: string } | undefined): string => {
            if (value?.color) {
                const hslaColor = rgbaToHsla(value.color)
                return `${name}: ${hslaColor};`
            }
            return ''
        }

        const cssVars: string[] = []

        // Handle page_bg for gradient
        if (is_gradient_background) {
            cssVars.push(`--background: ${page_bg.color};`)
        } else {
            cssVars.push(setThemeProperty('--background', page_bg))
        }

        // Set other theme properties
        cssVars.push(setThemeProperty('--muted-foreground', soft_text))
        cssVars.push(setThemeProperty('--foreground', text))
        cssVars.push(setThemeProperty('--card-foreground', text))
        cssVars.push(setThemeProperty('--card', card))
        cssVars.push(setThemeProperty('--popover-foreground', text))
        cssVars.push(setThemeProperty('--primary-foreground', text))
        cssVars.push(setThemeProperty('--secondary-foreground', text))
        cssVars.push(setThemeProperty('--accent-foreground', text))
        cssVars.push(setThemeProperty('--destructive-foreground', text))
        cssVars.push(setThemeProperty('--warning-foreground', text))
        cssVars.push(setThemeProperty('--info-foreground', text))
        cssVars.push(setThemeProperty('--success-foreground', text))
        cssVars.push(setThemeProperty('--primary', primary))
        cssVars.push(setThemeProperty('--secondary', secondary_bg))
        cssVars.push(setThemeProperty('--muted', softHighlight))
        cssVars.push(setThemeProperty('--accent', softHighlight))
        cssVars.push(setThemeProperty('--popover', secondary_bg))
        cssVars.push(setThemeProperty('--destructive', error))
        cssVars.push(setThemeProperty('--border', border))
        cssVars.push(setThemeProperty('--input', input))
        cssVars.push(setThemeProperty('--ring', ring))
        cssVars.push(setThemeProperty('--info', info))
        cssVars.push(setThemeProperty('--success', success))
        cssVars.push(setThemeProperty('--warning', warning))

        return {
            dataTheme: DEFAULT_DATA_THEME, // Keep base; CSS vars override
            cssVariables: cssVars.filter(Boolean).join(' '),
            enableNoiseTexture
        }
    } catch (error) {
        console.error('[generateThemeStyles] Error parsing theme:', error)
        return {
            dataTheme: DEFAULT_DATA_THEME,
            cssVariables: '',
            enableNoiseTexture: true
        }
    }
}

